import {testIdProps} from '@github-ui/test-id-props'
import {ActionList, Box, themeGet} from '@primer/react'
import {SuggestionItem} from './SuggestionItem'
import {useFilter, useInput, useSuggestions} from '../context'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {useMemo} from 'react'
import {getUniqueReactKey} from '../utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export const SuggestionsList = () => {
  const {id} = useFilter()
  const {
    suggestionsListRef,
    suggestionsContainerRef,
    activeSuggestion,
    isFetchingSuggestions,
    position,
    suggestions,
    suggestionsVisible,
    suggestionsHeading,
  } = useSuggestions()
  const {suspendFocus} = useInput()

  const prefetchingEnabled = isFeatureEnabled('filter_prefetch_suggestions')

  const skeletonRow = (key: string, animationStyle: 'wave' | 'pulse' = 'wave') => {
    return (
      <Box
        key={key}
        sx={{display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', lineHeight: '20px', mx: 2}}
      >
        <LoadingSkeleton variant="elliptical" width="20px" height="20px" animationStyle={animationStyle} />
        <LoadingSkeleton variant="rounded" width="random" height="16px" animationStyle={animationStyle} />
      </Box>
    )
  }

  const loadingSkeletons = useMemo(
    () => (
      <Box>
        {Array(8)
          .fill(1)
          .map((_, i) => skeletonRow(`loading-skeleton-${i}`))}
      </Box>
    ),
    [],
  )

  // When prefetching, we will only show a single loading skeleton
  const prefetchedSkeletons = useMemo(() => skeletonRow(`prefetched-loading-skeleton`, 'pulse'), [])

  const showSuggestionsOverlay = (suggestionsVisible && suggestions.length > 0) || isFetchingSuggestions

  return (
    <Box
      sx={{top: 5, left: 0, mt: 1, right: 0, zIndex: '999', position: 'absolute', backgroundColor: 'initial'}}
      {...testIdProps('backdrop-anchor')}
    >
      <Box
        id={`${id}-suggestions`}
        ref={suggestionsListRef}
        sx={{
          bg: 'canvas.overlay',
          borderRadius: '12px',
          boxShadow: 'overlay.shadow',
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          left: ['0', '0', `${position ? position.left : 0}px`],
          maxHeight: `calc(100vh - 2rem)`,
          minWidth: '250px',
          position: 'absolute',
          opacity: showSuggestionsOverlay ? 1 : 0,
          overflow: 'hidden',
          transition: 'opacity 0.2s ease',
          visibility: showSuggestionsOverlay ? 'visible' : 'hidden',
          whiteSpace: 'normal',
          width: ['100%', 'min-content', 'min-content'],
        }}
        onMouseDown={e => suspendFocus(e.currentTarget)}
      >
        <Box
          sx={{
            p: 0,
            flexGrow: 1,
            fontSize: themeGet('primer.text.body.size.large') ?? '14px',
          }}
        >
          <ActionList
            {...testIdProps('filter-results')}
            aria-label={isFetchingSuggestions ? 'Loading Suggestions' : 'Suggestions'}
            id={`${id}-results`}
            ref={suggestionsContainerRef}
            sx={{
              maxHeight: '20em',
              overflowX: 'hidden',
              overflowY: 'auto',
              listStyle: 'none',
              minHeight: '48px',
            }}
          >
            {!prefetchingEnabled && isFetchingSuggestions
              ? loadingSkeletons
              : suggestions.length > 0 && (
                  <ActionList.Group>
                    {suggestionsHeading && (
                      <ActionList.GroupHeading {...testIdProps('suggestions-heading')} as="h3">
                        {suggestionsHeading}
                      </ActionList.GroupHeading>
                    )}
                    {suggestions.map((s, i) => {
                      return (
                        <SuggestionItem
                          {...s}
                          suggestionId={i}
                          key={getUniqueReactKey('suggestion', s.id, `${s.value}-${s.displayName}-${s.description}`)}
                          active={i === activeSuggestion}
                        />
                      )
                    })}
                  </ActionList.Group>
                )}
            {prefetchingEnabled &&
              isFetchingSuggestions &&
              (suggestions.length > 0 ? prefetchedSkeletons : loadingSkeletons)}
          </ActionList>
        </Box>
      </Box>
    </Box>
  )
}

try{ SuggestionsList.displayName ||= 'SuggestionsList' } catch {}